// The /savedReport endpoint returns scheduled-report details as a free-text
// string shaped roughly like:
//
//   Attachment type: XLSX, Recepients: a@x.com, Email body: Test,
//   Schedule: {"minute":"0,40","hour":"0,9","dayOfMonth":"1,18",
//             "month":"1,11","dayOfWeek":"1,2"}, Active: true
//
// Parsing lives here so the row component can render a compact summary.

export interface ScheduleCron {
  minute?: string;
  hour?: string;
  dayOfMonth?: string;
  month?: string;
  dayOfWeek?: string;
}

export interface ParsedScheduledDetails {
  attachment?: string;
  recipients: string[];
  body?: string;
  schedule?: ScheduleCron;
  active: boolean;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// index 0 unused so month "1" → "Jan".
const MONTH_NAMES = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const parseScheduledDetails = (raw: string | undefined): ParsedScheduledDetails | null => {
  if (!raw) return null;

  let schedule: ScheduleCron | undefined;
  let withoutSchedule = raw;
  const scheduleMatch = raw.match(/Schedule:\s*(\{[^}]*\})/);
  if (scheduleMatch) {
    try {
      schedule = JSON.parse(scheduleMatch[1]);
    } catch {
      schedule = undefined;
    }
    withoutSchedule = raw.replace(scheduleMatch[0], '').replace(/,\s*,/g, ',');
  }

  const parts: Record<string, string> = {};
  withoutSchedule.split(',').forEach((piece) => {
    const idx = piece.indexOf(':');
    if (idx <= 0) return;
    const key = piece.slice(0, idx).trim();
    const value = piece.slice(idx + 1).trim();
    if (value) parts[key] = value;
  });

  const recipients = parts.Recepients
    ? parts.Recepients.split(/\s*;\s*/)
        .flatMap((s) => s.split(/\s+/))
        .filter(Boolean)
    : [];

  return {
    attachment: parts['Attachment type'],
    recipients,
    body: parts['Email body'],
    schedule,
    active: parts.Active === 'true',
  };
};

// Pad numeric strings to two digits; leave non-numerics (including the '*'
// cron wildcard) untouched so we never produce garbage like '0*:30'.
const padTwo = (n: string): string => (/^\d$/.test(n) ? `0${n}` : n);

const formatTimes = (hour: string, minute: string): string => {
  if (hour === '*' && minute === '*') return '';
  const hours = hour === '*' ? ['*'] : hour.split(',').map((h) => h.trim());
  const mins = minute === '*' ? ['*'] : minute.split(',').map((m) => m.trim());
  const out: string[] = [];
  for (const h of hours) {
    for (const m of mins) {
      out.push(`${padTwo(h)}:${padTwo(m)}`);
    }
  }
  return out.join(', ');
};

const formatDayOfWeek = (dow: string): string => {
  if (dow === '*') return '';
  return dow
    .split(',')
    .map((d) => d.trim())
    .map((d) => {
      const n = Number(d);
      return Number.isFinite(n) ? (DAY_NAMES[((n % 7) + 7) % 7] ?? d) : d;
    })
    .join(', ');
};

const formatMonthDay = (month: string, dayOfMonth: string): string => {
  const pieces: string[] = [];
  if (month !== '*') {
    const months = month
      .split(',')
      .map((m) => m.trim())
      .map((m) => {
        const n = Number(m);
        return Number.isFinite(n) && n >= 1 && n <= 12 ? MONTH_NAMES[n] : m;
      })
      .join(', ');
    pieces.push(months);
  }
  if (dayOfMonth !== '*') {
    const days = dayOfMonth
      .split(',')
      .map((d) => d.trim())
      .join(', ');
    pieces.push(`day ${days}`);
  }
  return pieces.join(' ');
};

export const formatScheduleSummary = (schedule: ScheduleCron | undefined): string => {
  if (!schedule) return '';
  const { minute = '*', hour = '*', dayOfMonth = '*', month = '*', dayOfWeek = '*' } = schedule;

  const pieces = [
    formatTimes(hour, minute),
    formatDayOfWeek(dayOfWeek),
    formatMonthDay(month, dayOfMonth),
  ].filter(Boolean);

  return pieces.join(' · ');
};

/** Compact recipient display: first address, then "+N more" when there are more. */
export const formatRecipients = (recipients: string[]): string => {
  if (recipients.length === 0) return '';
  if (recipients.length === 1) return recipients[0];
  return `${recipients[0]} +${recipients.length - 1} more`;
};
